import PropTypes from 'prop-types';

import { Link, matchPath, useLocation } from 'react-router';

import { useAppSelector as useSelector } from 'dux/app/hooks';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { Button } from 'Components/LegacyButton';

import { productsCategories } from 'constants/products';

import {
  getHasCompletedHaircareConsultation,
  getHasCompletedSkincareConsultation,
  getHasDraftHaircareConsultation,
  getHasDraftSkincareConsultation,
} from 'dux/user/selectors';

const StyledButton = styled(Button)`
  ${legacyTheme.breakpoints.up('md')} {
    width: 279px;
  }
`;

const useConsultationStatus = () => {
  const { pathname } = useLocation();
  const { category } = matchPath('/products/:category/*', pathname)?.params || {};
  const hasCompleteHaircareConsultation = useSelector(getHasCompletedHaircareConsultation);
  const hasCompleteSkincareConsultation = useSelector(getHasCompletedSkincareConsultation);
  const hasStartedHaircareConsultation = useSelector(getHasDraftHaircareConsultation);
  const hasStartedSkincareConsultation = useSelector(getHasDraftSkincareConsultation);

  const hasCompleteConsultation =
    (category === productsCategories.HAIRCARE && hasCompleteHaircareConsultation) ||
    (category === productsCategories.SKINCARE && hasCompleteSkincareConsultation);
  const hasStartedConsultation =
    (category === productsCategories.HAIRCARE && hasStartedHaircareConsultation) ||
    (category === productsCategories.SKINCARE && hasStartedSkincareConsultation);

  return {
    hasCompleteConsultation,
    hasStartedConsultation,
  };
};

const ProductConsultationCTA = ({ className, content, isFixed, noMargin }) => {
  const { hasCompleteConsultation, hasStartedConsultation } = useConsultationStatus();

  const ctaContent =
    content[
      hasCompleteConsultation
        ? 'hasCompletedConsultation'
        : hasStartedConsultation
        ? 'hasStartedConsultation'
        : 'hasNotStartedConsultation'
    ];

  return (
    <StyledButton
      className={className}
      Component={Link}
      data-click={ctaContent.dataClick}
      data-from={ctaContent.dataFrom}
      data-testid="product-page-consultation-cta"
      fixed={isFixed}
      noMargin={noMargin}
      to={ctaContent.url}
      variant="vert"
    >
      {ctaContent.label}
    </StyledButton>
  );
};

ProductConsultationCTA.propTypes = {
  content: PropTypes.shape({}).isRequired,
  dataClick: PropTypes.string,
  dataFrom: PropTypes.string,
  isFixed: PropTypes.bool,
  className: PropTypes.string,
  noMargin: PropTypes.bool,
};

ProductConsultationCTA.defaultProps = {
  dataClick: null,
  dataFrom: null,
  isFixed: false,
  className: null,
  noMargin: null,
};

export default ProductConsultationCTA;
